/**
 * Utility for safely delaying execution with automatic cleanup of previous delays.
 * Prevents multiple pending timers from stacking up and causing performance issues.
 */

import debug from 'utils/debug';

type DelayDuration =
  | { delayInMs: number }
  | { delayInMinutes: number }
  | { delayInSeconds: number };
type DelayOptions = {
  /** Unique identifier for the operation, will cancel existing timers if repeated before execution */
  id: string | number;
} & DelayDuration;

/**
 * Manager class for handling delayed executions
 */
class SafelyDelayExecutionManager {
  private timeoutMap: Map<string, ReturnType<typeof setTimeout>> = new Map();

  /**
   * Creates a promise that resolves after the specified delay time.
   * Automatically cancels any previous delay with the same ID.
   *
   * @param options Delay options with ID and one of delayInMs, delayInMinutes, or delayInSeconds
   * @returns Promise that resolves after the delay
   */
  async delay(options: DelayOptions): Promise<void> {
    const { id } = options;
    const delayInMs = 'delayInMs' in options ? options.delayInMs : undefined;
    const delayInMinutes = 'delayInMinutes' in options ? options.delayInMinutes : undefined;
    const delayInSeconds = 'delayInSeconds' in options ? options.delayInSeconds : undefined;

    // Calculate total delay in ms (prioritize in order: ms, minutes, seconds)
    const totaldelayInMs =
      delayInMs !== undefined
        ? delayInMs
        : delayInMinutes !== undefined
          ? delayInMinutes * 60_000
          : delayInSeconds !== undefined
            ? delayInSeconds * 1_000
            : 0;

    // Convert id to string for map keys
    const operationKey = String(id);

    // If no delay or zero delay, resolve immediately
    if (totaldelayInMs <= 0) {
      return;
    }

    // Cancel any existing timeout for this operation
    this.cancelDelay(operationKey);

    // Create and register new timeout
    return new Promise<void>(resolve => {
      const timeoutId = setTimeout(() => {
        // Auto-cleanup when resolved
        this.timeoutMap.delete(operationKey);
        debug.template.devEnvOnly(
          `✓ Delay completed for operation '${operationKey}' after ${totaldelayInMs}ms`
        );
        resolve();
      }, totaldelayInMs);

      // Store for potential cancellation
      this.timeoutMap.set(operationKey, timeoutId);
      debug.template.devEnvOnly(
        `⏱ Started delay for operation '${operationKey}' (${totaldelayInMs}ms)`
      );
    });
  }

  /**
   * Cancels an existing delay for the specified operation
   *
   * @param id The operation identifier to cancel
   * @returns true if a delay was cancelled, false otherwise
   */
  cancelDelay(id: string | number): boolean {
    const operationKey = String(id);
    const existingTimeout = this.timeoutMap.get(operationKey);

    if (existingTimeout) {
      clearTimeout(existingTimeout);
      this.timeoutMap.delete(operationKey);
      debug.template.devEnvOnly(`✗ Cancelled pending delay for operation '${operationKey}'`);
      return true;
    }

    return false;
  }

  /**
   * Check if there's a pending delay for a specific operation
   *
   * @param id The operation identifier to check
   * @returns true if a delay is active for this operation
   */
  hasActiveDelay(id: string | number): boolean {
    return this.timeoutMap.has(String(id));
  }

  /**
   * Get all active delay operation IDs
   *
   * @returns Array of operation IDs with active delays
   */
  getActiveDelayIds(): string[] {
    return Array.from(this.timeoutMap.keys());
  }
}

// Export singleton instance
export const safelyDelayExecutionManager = new SafelyDelayExecutionManager();

/**
 * Utility function to safely delay an operation
 *
 * This version automatically cancels any previous delay with the same ID.
 *
 * @param options Delay options with ID and one of delayInMs, delayInMinutes, or delayInSeconds
 */
export async function safelyDelayExecution(options: DelayOptions): Promise<void> {
  return safelyDelayExecutionManager.delay(options);
}

export default safelyDelayExecution;
